import { useCallback, useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type FeishuCredentials,
  type FeishuLongConnState,
  type FeishuLongConnectionInfo,
  type FeishuStatus,
  type RosterSyncResult,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import type { OrgDetailContext } from "./context.js";
import { InlineLoading, Spinner } from "../../components/Spinner.js";
import { ErrorBanner, InfoBanner } from "../../components/ErrorBanner.js";
import { StatusPill } from "../../components/StatusPill.js";
import { formatDateTime } from "../../lib/format.js";

const EMPTY_CREDS: FeishuCredentials = {
  appId: "",
  appSecret: "",
  encryptKey: "",
  verificationToken: "",
  tenantKey: "",
};

/** Phase G · 已 connected 页面状态轮询间隔（10s）— 跟踪 SDK 自动重连引发的 state 漂移 */
const STATUS_POLL_INTERVAL_MS = 10_000;

interface SuccessMsg {
  text: string;
  summary?: RosterSyncResult;
}

const LONGCONN_TONE: Record<
  FeishuLongConnState,
  | "longconn-idle"
  | "longconn-connecting"
  | "longconn-connected"
  | "longconn-reconnecting"
  | "longconn-failed"
> = {
  idle: "longconn-idle",
  connecting: "longconn-connecting",
  connected: "longconn-connected",
  reconnecting: "longconn-reconnecting",
  failed: "longconn-failed",
};

export function FeishuPanel(props: {
  /** 状态每次刷新（初始 + 10s 轮询 + 操作后）回调；组织搭建 wizard 用来判定就绪。 */
  onStatusChange?: (status: FeishuStatus) => void;
} = {}): JSX.Element | null {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const [status, setStatus] = useState<FeishuStatus | null>(null);
  // ref 持回调，避免回调身份变化触发轮询 effect 重建
  const onStatusChangeRef = useRef(props.onStatusChange);
  onStatusChangeRef.current = props.onStatusChange;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessMsg | null>(null);
  const [form, setForm] = useState<FeishuCredentials>(EMPTY_CREDS);
  const [busy, setBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  /**
   * fetchStatus 接两套调用：
   * - useEffect 初始拉 + 10s 轮询（带 cancelled flag 防 unmount 后 setState 警告）
   * - connect / sync / disconnect 完后主动 refetch（同步路径，不带 flag）
   */
  const fetchStatus = useCallback(async (): Promise<FeishuStatus | null> => {
    if (!auth) return null;
    try {
      const next = await apiCall<FeishuStatus>(
        "GET",
        `/api/organizations/${org.code}/feishu/status`,
        { token: auth.token },
      );
      setStatus(next);
      onStatusChangeRef.current?.(next);
      return next;
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "加载状态失败";
      setError(msg);
      return null;
    }
  }, [auth, org.code]);

  useEffect(() => {
    let cancelled = false;
    if (!auth) return;
    const load = async (): Promise<void> => {
      try {
        const next = await apiCall<FeishuStatus>(
          "GET",
          `/api/organizations/${org.code}/feishu/status`,
          { token: auth.token },
        );
        if (!cancelled) {
          setStatus(next);
          onStatusChangeRef.current?.(next);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.message : "加载状态失败";
        setError(msg);
      }
    };
    void load();
    // Phase G · 长连接状态可能在 admin 不操作时被 SDK 重连漂移 → 轮询刷新
    const timer = window.setInterval(() => void load(), STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [auth, org.code]);

  const handleSaveAndConnect = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!auth || busy) return;
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const credsBody: Record<string, string> = {
        appId: form.appId.trim(),
        appSecret: form.appSecret,
      };
      // 高级面板字段 — 仅 HTTP webhook 模式需要，留空则不传（schema 保持空）
      if (form.tenantKey?.trim()) credsBody.tenantKey = form.tenantKey.trim();
      if (form.encryptKey) credsBody.encryptKey = form.encryptKey.trim();
      if (form.verificationToken)
        credsBody.verificationToken = form.verificationToken.trim();
      await apiCall(
        "PUT",
        `/api/organizations/${org.code}/feishu/credentials`,
        { body: credsBody, token: auth.token },
      );
      const result = await apiCall<{ ok: true; summary: RosterSyncResult }>(
        "POST",
        `/api/organizations/${org.code}/feishu/connect`,
        { body: {}, token: auth.token },
      );
      setSuccess({
        text: `凭据已保存 · 首次 roster 同步完成 · 长连接正在后台建立`,
        summary: result.summary,
      });
      setForm(EMPTY_CREDS);
      await fetchStatus();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message || err.code : "连接失败";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleResync = async (): Promise<void> => {
    if (!auth || busy) return;
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const result = await apiCall<{ ok: true; summary: RosterSyncResult }>(
        "POST",
        `/api/organizations/${org.code}/feishu/sync`,
        { body: {}, token: auth.token },
      );
      setSuccess({ text: "同步完成", summary: result.summary });
      await fetchStatus();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "同步失败";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    if (!auth || busy) return;
    if (!confirmDisconnect) {
      setConfirmDisconnect(true);
      return;
    }
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      await apiCall(
        "DELETE",
        `/api/organizations/${org.code}/feishu/connection`,
        { token: auth.token },
      );
      setSuccess({ text: "已断开连接 · 长连接已关闭" });
      setConfirmDisconnect(false);
      await fetchStatus();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "断开失败";
      setError(msg);
      setConfirmDisconnect(false);
    } finally {
      setBusy(false);
    }
  };

  if (!auth) return null;

  return (
    <div className="space-y-6">
      <InfoBanner>
        <strong>长连接模式（推荐）：</strong>
        admin-portal 主动出 WebSocket 连接到飞书，
        <strong className="mx-1">无需公网 HTTPS、无需 webhook 签名校验</strong>
        。仅当你要切换到 HTTP webhook 模式时才需配置「高级」面板的字段
        （Tenant Key / Verification Token / Encrypt Key）。
      </InfoBanner>

      {error ? <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner> : null}
      {success ? (
        <div className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-800">
          <p className="font-medium">{success.text}</p>
          {success.summary ? (
            <p className="mt-1 text-emerald-700">
              部门 {success.summary.departments} · 已更新用户{" "}
              {success.summary.usersUpdated}
              {success.summary.errors.length > 0
                ? ` · 异常 ${success.summary.errors.length}`
                : ""}
            </p>
          ) : null}
        </div>
      ) : null}

      {status === null ? (
        <InlineLoading label="加载状态…" />
      ) : status.connected ? (
        <ConnectedView
          status={status}
          busy={busy}
          confirmDisconnect={confirmDisconnect}
          onResync={() => void handleResync()}
          onDisconnect={() => void handleDisconnect()}
          onCancelDisconnect={() => setConfirmDisconnect(false)}
        />
      ) : (
        <CredentialForm
          form={form}
          busy={busy}
          longConnection={status.longConnection}
          onChange={setForm}
          onSubmit={handleSaveAndConnect}
        />
      )}
    </div>
  );
}

function LongConnPill({
  info,
}: {
  info?: FeishuLongConnectionInfo;
}): JSX.Element {
  if (!info) {
    // 老 admin-portal（未启用 longConnManager）→ 退回旧 connected pill
    return <StatusPill status="connected" />;
  }
  const tone = LONGCONN_TONE[info.state];
  const label =
    info.state === "connected" && info.connectCount > 0
      ? `已连接 (${info.connectCount})`
      : undefined;
  return <StatusPill status={tone} label={label} />;
}

function ConnectedView({
  status,
  busy,
  confirmDisconnect,
  onResync,
  onDisconnect,
  onCancelDisconnect,
}: {
  status: FeishuStatus;
  busy: boolean;
  confirmDisconnect: boolean;
  onResync: () => void;
  onDisconnect: () => void;
  onCancelDisconnect: () => void;
}): JSX.Element {
  const lc = status.longConnection;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dosia-border bg-dosia-bg-light p-5">
        <div className="flex items-center justify-between pb-3">
          <h3 className="text-sm font-semibold text-dosia-text">连接状态</h3>
          <LongConnPill info={lc} />
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Cell
            label="长连接状态"
            value={
              lc
                ? `${lc.state} · 累计连上 ${lc.connectCount} 次`
                : "未启用 longConnManager"
            }
            tone={
              lc
                ? lc.state === "connected"
                  ? "ok"
                  : lc.state === "failed"
                    ? "warn"
                    : "default"
                : "default"
            }
          />
          <Cell
            label="最近建联时间"
            value={formatDateTime(lc?.lastConnectedAt) || "—"}
          />
          {lc?.lastReconnectingAt ? (
            <Cell
              label="最近重连尝试"
              value={formatDateTime(lc.lastReconnectingAt) || "—"}
              tone="warn"
            />
          ) : null}
          {lc?.lastError ? (
            <Cell label="最近错误" value={lc.lastError} tone="warn" />
          ) : null}
          <Cell label="Tenant Key" value={status.tenantKey ?? "—（长连接模式不需要）"} mono />
          <Cell label="飞书部门数" value={String(status.departmentCount)} />
          <Cell label="已绑用户数" value={String(status.boundUserCount)} />
          <Cell
            label="近 24 小时 webhook 异常"
            value={String(status.last24hErrors)}
            tone={status.last24hErrors > 0 ? "warn" : "ok"}
          />
        </dl>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onResync}
          className="rounded bg-dosia-accent px-3 py-1.5 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover disabled:opacity-60"
        >
          重新同步 roster
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDisconnect}
          className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-500/20 disabled:opacity-60"
        >
          {confirmDisconnect ? "确认断开（清凭据 + 关长连接）" : "断开连接"}
        </button>
        {confirmDisconnect ? (
          <button
            type="button"
            onClick={onCancelDisconnect}
            className="text-xs text-dosia-text-secondary hover:underline"
          >
            取消
          </button>
        ) : null}
        {busy ? <Spinner size="sm" /> : null}
      </div>
    </div>
  );
}

function CredentialForm({
  form,
  busy,
  longConnection,
  onChange,
  onSubmit,
}: {
  form: FeishuCredentials;
  busy: boolean;
  longConnection?: FeishuLongConnectionInfo;
  onChange: (next: FeishuCredentials) => void;
  onSubmit: (e: React.FormEvent) => void;
}): JSX.Element {
  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-lg border border-dosia-border bg-dosia-bg-light p-5"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-dosia-text">飞书凭据</h3>
        <LongConnPill info={longConnection} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CredField
          label="App ID *"
          value={form.appId}
          required
          autoComplete="off"
          onChange={(v) => onChange({ ...form, appId: v })}
        />
        <CredField
          label="App Secret *"
          value={form.appSecret}
          required
          type="password"
          autoComplete="new-password"
          onChange={(v) => onChange({ ...form, appSecret: v })}
        />
      </div>

      <details className="rounded border border-dosia-border bg-dosia-bg-lighter">
        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-dosia-text-secondary hover:text-dosia-text">
          高级 · HTTP webhook 模式（默认收起；长连接模式无需配置）
        </summary>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 pt-1">
          <CredField
            label="Tenant Key"
            value={form.tenantKey ?? ""}
            autoComplete="off"
            hint="仅 HTTP webhook 模式：按 tenant_key 反查 org（长连接模式可留空）"
            onChange={(v) => onChange({ ...form, tenantKey: v })}
          />
          <CredField
            label="Verification Token"
            value={form.verificationToken ?? ""}
            autoComplete="off"
            hint="仅 HTTP webhook 模式：飞书后台 url_verification 探针校验用"
            onChange={(v) => onChange({ ...form, verificationToken: v })}
          />
          <CredField
            label="Encrypt Key"
            value={form.encryptKey ?? ""}
            autoComplete="off"
            hint="仅 HTTP webhook 模式：webhook 签名校验密钥（本系统不实现 AES 解密）"
            onChange={(v) => onChange({ ...form, encryptKey: v })}
          />
        </div>
      </details>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded bg-dosia-accent px-4 py-1.5 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover disabled:opacity-60"
        >
          {busy ? <Spinner size="sm" className="text-dosia-bg-light" /> : null}
          保存并启动长连接
        </button>
      </div>
    </form>
  );
}

function CredField({
  label,
  value,
  hint,
  required,
  type = "text",
  autoComplete,
  onChange,
}: {
  label: string;
  value: string;
  hint?: string;
  required?: boolean;
  type?: string;
  autoComplete?: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-dosia-text-secondary">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text focus:border-dosia-accent focus:outline-none"
      />
      {hint ? <p className="text-xs text-dosia-text-dim">{hint}</p> : null}
    </label>
  );
}

function Cell({
  label,
  value,
  mono,
  tone = "default",
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "default" | "ok" | "warn";
}): JSX.Element {
  const toneClass =
    tone === "warn"
      ? "text-amber-800"
      : tone === "ok"
        ? "text-emerald-800"
        : "text-dosia-text";
  return (
    <div className="space-y-1">
      <dt className="text-xs uppercase tracking-wide text-dosia-text-secondary">
        {label}
      </dt>
      <dd className={mono ? `font-mono ${toneClass}` : toneClass}>{value}</dd>
    </div>
  );
}
